import { STORY_SOURCE, story } from '@/content/storyBuilder'

// Everything below comes from the four-part full-page capture of
// digitopia.co/customers/akcoat/ sent on 2026-08-28 — "tastamam şekilde,
// aynısını, tıpkısını tasarla" — with the one photograph on it, the chemical
// handler beside the drum pallets, which the reference runs under the
// standfirst. The film already sat in the header, which is where the reference
// puts it, so nothing above the fold changed but the lede.
//
// Its shape is the reference's: no grey panels anywhere, four left-ranged
// sections of the General Manager speaking in the first person, and no lists.
// The closing quote is the one thing this page does that no written page
// before it does — see `closingQuote`. digitopia.co runs Veysi Küçük's Hall of
// Fame words as the standfirst here and closes on a *different* statement of
// his, so both are on the page and neither is printed twice.
export const added = story('akcoat', 'Akcoat', {
  doc: '4548eebf-fca1-418d-95ab-0487eb590459-Akcoat and Digitopia.pdf',
  video: 'mLIlXTvNQo4',
  lede: ['What gets measured gets managed.'],
  intro:
    '“By cultivating a brainstorming environment with the participation of colleagues from all '
    + 'departments, our awareness of digital transformation was maximized. Akcoat determined its '
    + 'digital maturity level objectively by scoring the company thanks to the excellent support '
    + 'and methodology Digitopia provided to Akcoat.”',
  photo: {
    src: '/photo/akcoat-chemical-handling-drums.webp',
    alt: 'A worker in a full yellow chemical protection suit and hood handling a nozzle in front '
      + 'of pallets of shrink-wrapped blue drums and racks of gas cylinders.',
  },
  sections: [
    {
      heading: 'Who is Akcoat?',
      body: [
        'Akcoat has been operating globally for almost half a century in five different product '
        + 'groups; these are enamel, ceramic, glass, non-stick & decorative coatings, and '
        + 'pigment. It is the world’s second-largest enamel coating manufacturer and exports to '
        + 'more than 65 countries.',
        'The company has two production facilities in Spain and Turkey. Also, three subsidiaries '
        + 'are located in the USA, China, Mexico, and has teams located in ten different '
        + 'countries around the world. Its biggest market is China, and the USA comes in second.',
        'Akcoat is a global solution partner of a wide range of manufacturers such as white '
        + 'goods, small household appliances, building materials, architectural solutions, '
        + 'kitchenware, and telecommunication. The signature of Akcoat can be seen on many '
        + 'products that are used in almost all residential and commercial spaces that make '
        + 'daily life easier.',
        'Akcoat journey, your works, your goals.',
      ],
    },
    {
      heading: 'Project Background',
      body: [
        'While planning such a comprehensive process like “digitalisation”, which directly '
        + 'affects all the structures and activities of organizations, we aimed to understand '
        + 'where we are in this field with the idea that “what gets measured gets managed”. '
        + 'Afterwards, we began working with Digitopia to seek answers to our questions together '
        + 'with our consultant.',
        'By cultivating a brainstorming environment with the participation of colleagues from '
        + 'all departments, our awareness of digital transformation was maximized. Akcoat '
        + 'determined its digital maturity level objectively by scoring the company thanks to '
        + 'the excellent support and methodology Digitopia provided to Akcoat. This evaluation '
        + 'and the roadmap prepared have been a very valuable guide to Akcoat.',
      ],
    },
    {
      heading: 'Benefits of Digital Maturity Index',
      body: [
        'Thanks to the digital maturity study, Akcoat has clarified some fuzzy concepts from its '
        + 'own perspective. While determining the company’s pros and cons, both in the past and '
        + 'today, Akcoat also determined its future goals and decided where it wants to go, '
        + 'which is called AkRoad. I believe that for a successful strategy all steps need to be '
        + 'fed with the right data and information, implemented with sufficient and correct '
        + 'resources, and constantly reviewed. In this direction, Akcoat prepared its two-year '
        + 'digitalization project plan, which aims to bring Akcoat to its strategic goals '
        + 'quickly.',
        'Our people-dimension score according to the digital maturity index is above average, '
        + 'thanks to our collaborative team culture. In this context, I can say that the most '
        + 'important anecdote in the process is the adoption of the team understanding that '
        + 'supports each other in every sense, from leadership teams to junior employees. One of '
        + 'the most important points in Akcoat’s digital maturity level measurement was the '
        + 'awareness of all leaders about their strengths and development areas with great '
        + 'sincerity and openness.',
      ],
    },
    {
      heading: 'From Now On…',
      body: [
        'Akcoat has launched a cultural change project. What do we mean by the concept of '
        + 'cultural change? People and culture are at the center of its strategy. Akcoat’s human '
        + 'resources will be the mainstay of re-evaluating the company’s corporate culture and '
        + 'performance in digital transformation as it is not possible to implement any strategy '
        + 'without starting from this point. Currently, the company’s goal is to proceed step by '
        + 'step in the direction of the guide we prepared as a result of the digital maturity '
        + 'test and to adapt to the requirements of the changing world with up-to-date plans '
        + 'compatible with new conditions. At first, Akcoat will focus on multi-dimensionally '
        + 'applicable and achievable improvement targets in line with the sectors in which we '
        + 'operate.',
        'Akcoat as a global one-stop solution partner will provide cost-effective and '
        + 'customer-oriented services that create more added value with digital catalogues, '
        + 'AR-supported product designs and online fast technical services. Those services, '
        + 'catalogues and other marketing activities will be measured and analyzed by experience '
        + 'management to increase valued and memorable customer interactions.',
        'One of the requirements of a customer-oriented company is to have demand forecasting '
        + 'management, so we started to make improvements in the system by taking it as our '
        + 'priority.',
        'It was one of the first areas the company acted because of the critical importance of '
        + 'the achievements here for Akcoat.',
        'The long-term goals of the company are to raise the bar for innovation in the industry. '
        + 'That’s why Akcoat is always working on disruptive innovations such as AI-supported '
        + 'product formulations, digital twins and product development, automation and IoT '
        + 'applications and their analysis.',
        'Digitalization of our business will help us to improve the efficiency of our process, '
        + 'consistency, and quality to sustain a one-stop solution partner vision with a '
        + 'long-term strategy.',
        'In order to sum up this conversation in one sentence: “what gets measured gets '
        + 'managed.”',
      ],
    },
  ],
  closingQuote: [
    'Akcoat has a great success story as one of the world’s leading companies in the coating '
    + 'sector. My goal is to raise this flag higher and take global leadership. Our company '
    + 'allocates significant resources to R&D, marketing, and talent management. To support '
    + 'this, we encourage all the colleagues to internalize the corporate values and to recruit '
    + 'candidates who are volunteers to be a part of this success story.',
  ],
  check:
    'This page’s words were transcribed from a four-part full-page capture of '
    + `${STORY_SOURCE}/akcoat/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — particularly the figures under “Who '
    + 'is Akcoat?” (“almost half a century”, “second-largest enamel coating manufacturer”, “more '
    + 'than 65 countries”, two plants, three subsidiaries, ten countries) and the roadmap’s name, '
    + 'AkRoad. One arithmetic oddity is the reference’s own and was copied rather than corrected: '
    + 'it says three subsidiaries and then names three countries, but writes the sentence as if '
    + 'the plants and the subsidiaries were one list. Three deliberate differences from the '
    + 'reference, each of them a template-wide decision rather than this page’s: the header '
    + 'eyebrow reads “Digitopia / Customer Success Story” where digitopia.co reads “SUCCESS '
    + 'STORIES”; the reference’s “Download” button at the foot of the story is here the one '
    + '“Read the full story (PDF)” button in the header, pointing at the same document; and the '
    + 'standfirst quote runs in the grey band every other story page uses, where the reference '
    + 'sets it as centred text on white. Two things to look at rather than decide. The closing '
    + 'card carries the General Manager’s words about the company (“raise this flag higher”), '
    + 'which is what the reference closes on, rather than the Hall of Fame quote — that one is '
    + 'the standfirst at the top of this page, exactly as on the reference, so both are here and '
    + 'neither is printed twice; the Hall of Fame page itself is untouched. And a colour '
    + 'photograph of Veysi Küçük at 600×600 was supplied with the capture: the card keeps the '
    + '900×900 transparent cut-out already on file, because it is larger and this template draws '
    + 'the figure standing on the card’s bottom edge, which a white-background photograph cannot '
    + 'do. One production note: the photograph is a 1200×455 original, which is what the live '
    + 'site serves, so it is drawn at that width and will look soft on a high-density screen '
    + 'until a larger file arrives.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 13
